import { useCallback, useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ListOrdered, MousePointerClick, Package, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { AnalyticsComparisonWindow, AnalyticsOverview, AnalyticsProductRow, compareMetricValues } from '@/services/analyticsMetrics';
import {
  FirstPartyAnalyticsRange,
  getFirstPartyAnalyticsOverviewComparison,
  getProductAnalytics,
} from '@/services/analyticsService';
import {
  AnalyticsEmptyState,
  AnalyticsErrorState,
  AnalyticsLoadingState,
  AnalyticsPageHeader,
  AnalyticsRefreshControl,
  ChartCard,
  MetricCard,
} from '@/components/analytics/AnalyticsShared';
import { useAnalyticsAutoRefresh } from '@/hooks/useAnalyticsAutoRefresh';
import { ANALYTICS_COLORS, formatDeltaText, formatNumber, formatUtcRange } from '@/components/analytics/analyticsDisplay';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/i18n';

export default function ProductAnalytics() {
  const [range, setRange] = useState<FirstPartyAnalyticsRange>('7d');
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [previousOverview, setPreviousOverview] = useState<AnalyticsOverview | null>(null);
  const [comparisonWindow, setComparisonWindow] = useState<AnalyticsComparisonWindow | null>(null);
  const [products, setProducts] = useState<AnalyticsProductRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();

  const loadAnalytics = useCallback(async (silent = false) => {
    try {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const [overviewResult, productResult] = await Promise.all([
        getFirstPartyAnalyticsOverviewComparison(range),
        getProductAnalytics(range, 15),
      ]);

      setOverview(overviewResult.current);
      setPreviousOverview(overviewResult.previous);
      setComparisonWindow(overviewResult.window);
      setProducts(productResult);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('analyticsLoadFailed');
      setError(message);
      if (!silent) toast.error(message);
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [range, t]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const silentRefresh = useCallback(() => loadAnalytics(true), [loadAnalytics]);
  useAnalyticsAutoRefresh(silentRefresh);

  const topProduct = products[0];
  const productsWithNames = products.filter((product) => product.name !== product.productId).length;
  const comparisonLabel = t('analyticsComparedWithPrior');

  return (
    <div className="space-y-6 animate-fade-in">
      <AnalyticsPageHeader
        title={t('analyticsProductsTitle')}
        description={t('analyticsProductsDescription')}
        range={range}
        onRangeChange={setRange}
        rangeLabel={t('analyticsDateRange')}
      >
        {overview?.start && (
          <p className="mt-2 text-xs text-muted-foreground">
            {t('analyticsUtcRange', { range: formatUtcRange(overview.start, overview.end) })}
          </p>
        )}
        {comparisonWindow?.previous && (
          <p className="mt-1 text-xs text-muted-foreground">
            {t('analyticsPriorUtcRange', { range: formatUtcRange(comparisonWindow.previous.start, comparisonWindow.previous.end) })}
          </p>
        )}
        <AnalyticsRefreshControl
          refreshLabel={t('analyticsRefresh')}
          hintLabel={t('analyticsAutoRefreshHint')}
          refreshing={refreshing}
          disabled={loading}
          onRefresh={silentRefresh}
        />
      </AnalyticsPageHeader>

      {loading ? (
        <AnalyticsLoadingState label={t('analyticsLoading')} />
      ) : error ? (
        <AnalyticsErrorState
          title={t('analyticsErrorTitle')}
          message={error}
          retryLabel={t('analyticsRetry')}
          onRetry={() => loadAnalytics()}
        />
      ) : products.length === 0 ? (
        <AnalyticsEmptyState title={t('analyticsNoEventsTitle')} description={t('analyticsNoProductEventsDescription')} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              title={t('analyticsProductClicks')}
              value={formatNumber(overview?.productClicks || 0)}
              description={t('analyticsProductClicksDescription')}
              icon={Package}
              comparisonLabel={comparisonLabel}
              comparisonValue={formatDeltaText(compareMetricValues(overview?.productClicks, previousOverview?.productClicks), {
                newLabel: t('analyticsComparisonNew'),
                noBaselineLabel: t('analyticsComparisonNoBaseline'),
              })}
            />
            <MetricCard
              title={t('analyticsAffiliateClicks')}
              value={formatNumber(overview?.affiliateClicks || 0)}
              description={t('analyticsAffiliateClicksDescription')}
              icon={MousePointerClick}
              comparisonLabel={comparisonLabel}
              comparisonValue={formatDeltaText(compareMetricValues(overview?.affiliateClicks, previousOverview?.affiliateClicks), {
                newLabel: t('analyticsComparisonNew'),
                noBaselineLabel: t('analyticsComparisonNoBaseline'),
              })}
            />
            <MetricCard
              title={t('analyticsTopClickedProduct')}
              value={topProduct?.name || '--'}
              description={t('analyticsTopClickedProductDescription')}
              icon={ListOrdered}
            />
            <MetricCard
              title={t('analyticsNoCtrTitle')}
              value={t('analyticsCountsOnly')}
              description={t('analyticsNoCtrDescription')}
              icon={ShieldAlert}
            />
          </div>

          <ChartCard title={t('analyticsTopProductsChart')} description={t('analyticsTopProductsChartDescription')}>
            <div className="h-[360px]" role="img" aria-label={t('analyticsTopProductsChart')}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={products.slice(0, 10)}
                  layout="vertical"
                  margin={{ top: 10, right: 20, bottom: 10, left: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={130}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => String(value).slice(0, 18)}
                  />
                  <Tooltip formatter={(value: number) => [formatNumber(value), t('analyticsProductClicks')]} />
                  <Bar dataKey="productClicks" name={t('analyticsProductClicks')} fill={ANALYTICS_COLORS[1]} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard
            title={t('analyticsProductRanking')}
            description={t('analyticsProductRankingDescription', { count: productsWithNames })}
          >
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('analyticsProduct')}</TableHead>
                    <TableHead>{t('brand')}</TableHead>
                    <TableHead>{t('category')}</TableHead>
                    <TableHead className="text-right">{t('analyticsProductClicks')}</TableHead>
                    <TableHead className="text-right">{t('analyticsAffiliateClicks')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.productId}>
                      <TableCell>
                        <div className="max-w-[260px]">
                          <p className="truncate font-medium">{product.name}</p>
                          <p className="truncate text-xs text-muted-foreground">{product.productId}</p>
                        </div>
                      </TableCell>
                      <TableCell>{product.brand}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-background/50">
                          {product.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{formatNumber(product.productClicks)}</TableCell>
                      <TableCell className="text-right">{formatNumber(product.affiliateClicks)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </ChartCard>
        </>
      )}
    </div>
  );
}
